import { Component, OnInit, Input, ElementRef, ViewChild, Output, EventEmitter, TemplateRef } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Subject, takeUntil, forkJoin, of } from "rxjs";
import { map, catchError } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { COL_TYPES, FieldConfig } from "./table.interface";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { RestApiService } from "src/app/core/services/rest-api.service";
import { DynamicFilterComponent } from "../dynamic-table-filter/dynamic-filter.component";
import { AuthenticationService } from "src/app/core/services/auth.service";
import { LectureSeuleService } from "src/app/core/services/lecture-seule.service";

@Component({
	selector: "app-table",
	templateUrl: "./table.component.html",
})
export class TableComponent implements OnInit {
	@Input() collection: string;
	@Input() columns: any[];
	@Input() options: any;
	@Input() useCustomEditModal: boolean = false;
	@Input() customActionsTemplate: TemplateRef<any>;
	@Input() canEditFn: (item: any) => boolean;
	@Input() canDeleteFn: (item: any) => boolean;
	@Input() hideSearch: boolean = false;
	title: string;
	editModalSize: string;
	@Output() dataFetchedCount = new EventEmitter<number>();
	@Output() onEdit = new EventEmitter<number>();
	@ViewChild("filterField", { static: true }) filterField: ElementRef;
	@ViewChild("filterValue", { static: true }) filterValue: ElementRef;
	data: any[] = [];
	dataLoading: boolean;
	sortField: string = "id";
	sortOrder: "desc" | "asc" = "desc";
	filterForm: FormGroup;
	backendUrl = environment.backendURL;
	page: number = 1;
	pageSize: number = 20;
	pageTotal: number = 0;
	COL_TYPES = COL_TYPES;
	editForm: FormGroup;
	modalEditRef: NgbModalRef;
	relationData = {};
	@ViewChild("modalEdit", { static: true }) modalEdit: ElementRef;
	unsubscriber: Subject<any> = new Subject<any>();
	searchResults: any[] = [];
	fields: { name: string; label: string; type: string }[] = [];
	fullFilter = "";
	isHidden = true;
	@Output() enableDynamicFilter = false;
	@ViewChild("filterChild") filterChild: DynamicFilterComponent;

	constructor(
		private fb: FormBuilder,
		private restApiService: RestApiService,
		private modalService: NgbModal,
        private auteService:AuthenticationService,
		private lectureSeuleService: LectureSeuleService,
	) {}

	/** Sur un écran en consultation seule, la colonne d'actions ne propose rien. */
	get isLectureSeule(): boolean {
		return this.lectureSeuleService.isLectureSeule;
	}

	ngOnInit(): void {
		this.restApiService.collectionSubject.subscribe((params: any) => {
			if (params && params.collection != undefined) {
				this.collection = params?.collection;
				this.title = params?.title;
				this.columns = params?.columns;
				this.editModalSize = params?.editModalSize;
				this.options = params?.options;
				this.fields = this.columns.map((column) => ({ name: column.name, label: column.label, type: column.type }));
				this.fetchData();
				this.fetchRelationData();
			}
		});
	}

	fetchData(): void {
		this.dataLoading = true;
		// Combine filters from options (keyword search) and fullFilter (dynamic filter)
		let combinedFilters = "";
		if (this.options?.filters && this.fullFilter) {
			combinedFilters = `${this.options.filters}&${this.fullFilter}`;
		} else if (this.options?.filters) {
			combinedFilters = this.options.filters;
		} else if (this.fullFilter) {
			combinedFilters = this.fullFilter;
		}

		this.restApiService
			.get(this.collection, {
				populate: this.options?.populate,
				filters: combinedFilters,
				sort: { field: this.sortField, order: this.sortOrder },
				pagination: { page: this.page, pageSize: this.pageSize },
			})
			.pipe(takeUntil(this.unsubscriber))
			.subscribe({
				next: (response) => {
					this.data = response.data;
					this.pageTotal = response.meta.pagination.total;
					// Enrich articles with maxAchat from GET /articles/max-achat/:code
					if (this.collection === "articles" && this.data?.length&&(this.auteService.isAdmin()||this.auteService.isChiffrage() )) {
						forkJoin(
							this.data.map((item: any) =>
								this.restApiService.getCustom(`articles/max-achat/${encodeURIComponent(item.code || "")}`).pipe(
									map((res: any) => ({ code: item.code, maxAchat: res?.data?.maxAchat ?? null })),
									catchError(() => of({ code: item.code, maxAchat: null })),
								),
							),
						)
							.pipe(takeUntil(this.unsubscriber))
							.subscribe({
								next: (results: any[]) => {
									results.forEach((r, i) => {
										if (this.data[i]) this.data[i].maxAchat = r.maxAchat;
									});
									this.dataLoading = false;
									this.dataFetchedCount.emit(this.pageTotal);
								},
								error: () => {
									this.dataLoading = false;
									this.dataFetchedCount.emit(this.pageTotal);
								},
							});
					} else {
						this.dataLoading = false;
						this.dataFetchedCount.emit(this.pageTotal);
					}
				},
				error: (error) => {
					this.dataLoading = false;
				},
			});
	}

	onSort(colName: string): void {
		const col = this.columns.find((c) => c.name === colName);
		let field = col?.sortField ?? colName;
		// For relation columns without explicit sortField, derive from displayField
		if (!col?.sortField && col?.type === COL_TYPES.RELATION && col?.relation?.displayField) {
			const df = col.relation.displayField;
			const firstField = Array.isArray(df) ? df[0] : df;
			field = `${colName}.${firstField}`;
		}
		if (this.sortField === field) {
			this.sortOrder = this.sortOrder === "desc" ? "asc" : "desc";
		} else {
			this.sortField = field;
			this.sortOrder = "desc";
		}
		this.fetchData();
	}

	openModal(editId?) {
		if (editId != undefined) {
			if (this.useCustomEditModal) {
				// Emit event for parent component to handle
				this.onEdit.emit(editId);
				return;
			}
			this.editForm = this.fb.group({ id: this.fb.control(editId) });
			this.restApiService
				.get(this.collection, { filters: `filters[id]=${editId}`, populate: this.options?.populate })
				.pipe(takeUntil(this.unsubscriber))
				.subscribe((response) => {
					const editData = response.data[0];
					this.getEditableColumns().forEach((item) => {
						if (item.type === COL_TYPES.RELATION) {
							const relatedItemId = editData[item.name]?.id;
							this.editForm.addControl(item.name, this.fb.control(relatedItemId));
						} else {
							this.editForm.addControl(item.name, this.fb.control(editData[item.name]));
						}
					});
					this.modalEditRef = this.modalService.open(this.modalEdit, { size: this.editModalSize, centered: true });
				});
		} else {
			this.editForm = this.fb.group({});
			this.getEditableColumns().forEach((item) => {
				this.editForm.addControl(item.name, this.fb.control("", Validators.required));
			});
			this.modalEditRef = this.modalService.open(this.modalEdit, { size: this.editModalSize, centered: true });
		}
	}

	onFormSubmit() {
		if (this.editForm.get("id")?.value == null) {
			this.restApiService
				.post(this.collection, this.editForm.getRawValue())
				.pipe(takeUntil(this.unsubscriber))
				.subscribe((response) => {
					this.fetchData();
					this.modalEditRef.close();
				});
		} else {
			this.restApiService
				.put(this.collection, this.editForm.get("id").value, this.editForm.getRawValue())
				.pipe(takeUntil(this.unsubscriber))
				.subscribe((response) => {
					this.fetchData();
					this.modalEditRef.close();
				});
		}
	}

	onDelete(id) {
		if (confirm("Êtes vous sûr de vouloir supprimer ?")) {
			this.restApiService
				.delete(this.collection, id)
				.pipe(takeUntil(this.unsubscriber))
				.subscribe((response) => {
					this.fetchData();
				});
		}
	}

	onPaginate(page: number) {
		this.page = page;
		this.fetchData();
		window.scrollTo({ top: 0, behavior: "smooth" });
	}

	getRelationValue(item: any, col: FieldConfig): any {
		if (col.type === COL_TYPES.RELATION && col.relation) {
			const displayField = col.relation.displayField;
			// Support array of display fields (e.g., ["nom", "prenom"])
			if (Array.isArray(displayField)) {
				const values = displayField.map((field: string) => this.getNestedValue(item, `${col.name}.${field}`)).filter((v: any) => v != null && v !== "");
				return values.join(" ");
			}
			const path = `${col.name}.${displayField}`;
			return this.getNestedValue(item, path);
		}
		return undefined;
	}

	getNestedValue(obj: any, path: string): any {
		return path.split(".").reduce((acc, part) => (acc ? acc[part] : undefined), obj);
	}

	getObjectDisplayValue(item: any, col: FieldConfig): any {
		const v = this.getNestedValue(item, col.name);
		return v == null || v === "" ? "----" : v;
	}

	transformRelationalData(data: any): any {
		const transformed = { ...data };
		this.columns.forEach((column) => {
			if (column.type === COL_TYPES.RELATION && column.relation) {
				if (transformed[column.name]) {
					transformed[column.name] = { id: transformed[column.name] };
				}
			}
		});
		return transformed;
	}

	getEditableColumns() {
		return this.columns.filter((e) => e.type !== COL_TYPES.ACTION && e.type !== COL_TYPES.OBJECT && e?.editable !== false);
	}

	getDropDownItems(columnName: string) {
		return this.relationData[columnName] || [];
	}

	fetchRelationData() {
		const relationColumns = this.columns.filter((col) => col.type === COL_TYPES.RELATION && col.relation);
		relationColumns.forEach((col) => {
			this.restApiService
				.get(col.relation.collection, {})
				.pipe(takeUntil(this.unsubscriber))
				.subscribe((response) => {
					this.relationData[col.name] = response.data;
				});
		});
	}

	fetchRelationalData() {
		this.columns.forEach((column) => {
			if (column.type === COL_TYPES.RELATION && column.relation) {
				this.restApiService
					.get(column.relation.collection, {})
					.pipe(takeUntil(this.unsubscriber))
					.subscribe((response) => {
						this.relationData[column.name] = response.data;
					});
			}
		});
	}

	onSearchResults(fullFilter: string) {
		this.fullFilter = fullFilter;
		this.fetchData();
	}
	onOffFilter() {
		return (this.isHidden = !this.isHidden);
	}
	clearFilter() {
		this.fullFilter = "";
		this.filterChild.clearFilter();
	}

	getBadgeClass(value: string, badgeConfig: any[]): string {
		if (!badgeConfig) return "";
		const badge = badgeConfig.find((b) => b.value === value);
		return badge ? badge.class : "bg-secondary-subtle text-secondary";
	}

	getBadgeLabel(value: string, badgeConfig: any[]): string {
		if (!badgeConfig) return value;
		const badge = badgeConfig.find((b) => b.value === value);
		return badge ? badge.label : value;
	}

	viewFile(file: any) {
		if (file?.url) {
			window.open(this.backendUrl + file.url, "_blank");
		}
	}

	canEdit(item: any): boolean {
		return this.canEditFn ? this.canEditFn(item) : true;
	}

	canDelete(item: any): boolean {
		return this.canDeleteFn ? this.canDeleteFn(item) : true;
	}
	/* fetchSchema() {
		this.restApiService.get(`${this.collection}/schema`, { jwt: "" }).subscribe((response) => {
			console.log(response);
		});
	}
	===================================================
	/* addData(data: any): Observable<any> {
		return this.restApiService.addData(this.collection, data);
	}

	updateData(data: any): Observable<any> {
		return this.restApiService.updateData(this.collection, data, data.id);
	}

	deleteData(id: number): Observable<any> {
		return this.restApiService.deleteData(this.collection, id);
	}

	getControlType(field: FieldConfig): string {
		return field.type || "text"; // Default to text input
	}

	getFilterControls() {
		return Object.keys(this.filterForm.controls);
	}

	getField(fieldName: string): FieldConfig | undefined {
		return this.columns.find((field) => field.name === fieldName);
	}

	onSelectFieldFilter(fieldName?: string) {
		const field = this.filterForm.get("filterField")?.value;
	}

	onAddFilter(): void {
		this.filterForm.addControl(this.filterField.nativeElement.value, this.fb.control(this.filterValue.nativeElement.value));
		this.filterValue.nativeElement.value = "";
	}

	onRemoveFilter(fieldName: string) {
		this.filterForm.removeControl(fieldName);
	}

	onApplyFilters(): void {
		const filters = this.filterForm.value;
		this.options?.filters = filters;
		this.fetchData();
	}

	onClearFilters(): void {
		this.filterForm.reset();
		this.options?.filters = {};
		this.fetchData();
	} */

	ngOnDestroy(): void {
		this.unsubscriber.next(null);
		this.unsubscriber.complete();
		// Clear shared config so next page's table does not show previous list (e.g. demandes → note de frais)
		this.restApiService.collectionSubject.next(null);
	}
}
